import { types, flow, getRoot } from 'mobx-state-tree';

/**
 * Минималистичный AuthStore - только управление сессией
 * Данные пользователя берутся из коллекции users через computed
 */
const AuthStore = types
    .model('AuthStore', {
        // Только ID текущего пользователя
        currentUserId: types.maybeNull(types.string),
        isAuthenticated: types.optional(types.boolean, false),
        isLoading: types.optional(types.boolean, false),
        error: types.maybeNull(types.string),
    })
    .views(self => ({
        // Computed ссылка на пользователя из коллекции users
        get user() {
            if (!self.currentUserId) return null;
            
            const root = getRoot(self);
            const usersCollection = root.models?.getCollectionManager?.('users');
            return usersCollection?.getById?.(self.currentUserId) || null;
        },
        
        // Для совместимости со старым кодом
        get username() {
            return self.user?.username || self.user?.email;
        },
        
        get userRole() {
            return self.user?.role;
        }
    }))
    .actions(self => {
        // Приватные методы
        const saveSession = () => {
            try {
                if (self.currentUserId) {
                    localStorage.setItem('auth-session', JSON.stringify({
                        userId: self.currentUserId,
                        isAuthenticated: self.isAuthenticated
                    }));
                }
            } catch (e) {
                console.error('Failed to save session:', e);
            }
        };
        
        const clearSession = () => {
            try {
                localStorage.removeItem('auth-session');
            } catch (e) {
                console.error('Failed to clear session:', e);
            }
        };
        
        const restoreSession = flow(function* () {
            try {
                const sessionStr = localStorage.getItem('auth-session');
                
                if (sessionStr) {
                    const session = JSON.parse(sessionStr);
                    self.currentUserId = session.userId;
                    self.isAuthenticated = true;
                }
            } catch (e) {
                console.error('Failed to restore session:', e);
                clearSession();
            }
        });
        
        return {
            // Публичные действия
            login: flow(function* (username, password) {
                self.isLoading = true;
                self.error = null;
                
                try {
                    const { apiClient } = getRoot(self);
                    const response = yield apiClient.post('/api/auth/login', { username, password });
                    
                    // Сохраняем только ID пользователя
                    self.currentUserId = response.user.id;
                    self.isAuthenticated = true;
                    
                    saveSession();
                    
                    // Загружаем модели и данные пользователя после авторизации
                    const root = getRoot(self);
                    if (root.models) {
                        yield root.models.loadModels();
                        
                        // Загружаем коллекцию users чтобы получить полные данные
                        const usersCollection = root.models.getCollectionManager('users');
                        if (usersCollection) {
                            yield usersCollection.load();
                        }
                    }
                    
                    return true;
                } catch (error) {
                    self.error = error.message || 'Ошибка авторизации';
                    throw error;
                } finally {
                    self.isLoading = false;
                }
            }),
            
            checkSession: flow(function* () {
                try {
                    const { apiClient } = getRoot(self);
                    const response = yield apiClient.get('/api/auth/session');
                    
                    if (response.authenticated && response.user) {
                        self.currentUserId = response.user.id;
                        self.isAuthenticated = true;
                        saveSession();
                        
                        // Загружаем модели и коллекцию users
                        const root = getRoot(self);
                        if (root.models) {
                            yield root.models.loadModels();
                            const usersCollection = root.models.getCollectionManager('users');
                            if (usersCollection) {
                                yield usersCollection.load();
                            }
                        }
                    } else {
                        self.currentUserId = null;
                        self.isAuthenticated = false;
                        clearSession();
                    }
                    
                    return response.authenticated;
                } catch (error) {
                    console.error('Session check failed:', error);
                    self.currentUserId = null;
                    self.isAuthenticated = false;
                    clearSession();
                    return false;
                }
            }),
            
            logout: flow(function* () {
                try {
                    const { apiClient } = getRoot(self);
                    yield apiClient.post('/api/auth/logout');
                } catch (error) {
                    console.error('Logout error:', error);
                } finally {
                    self.currentUserId = null;
                    self.isAuthenticated = false;
                    self.error = null;
                    clearSession();
                    
                    const root = getRoot(self);
                    if (root.reset) {
                        root.reset();
                    }
                }
            }),
            
            // Lifecycle hooks
            afterCreate: flow(function* () {
                yield restoreSession();
            }),
            
            reset() {
                self.currentUserId = null;
                self.isAuthenticated = false;
                self.isLoading = false;
                self.error = null;
            }
        };
    });

export { AuthStore };